import "./JobHistoryComponent.scss";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import JobService from "../../services/JobService";
import { JobOrder, TripHistoryStats } from "../../interfaces/job.interface";
import SearchBar from "../SearchBar/SearchBar";
import DateTimePickerComponent from "../DateTimePicker/DateTimePicker";
import Modal from "../Modal/Modal";
import JobDetails from "./JobDetails";
import TransporterDetails from "../TransporterDetails";
import ContainerDetails from "./ContainerDetails";
import { formatEpochDate } from "../../utils/general";
import { useLoader } from "../../contexts/LoaderContext";
import StatsBoxContainer from "../StatsBoxContainer/StatsBoxContainer";
import TableComponent, {
  highlightText,
} from "../TableComponent/TableComponent";
import { SelectChangeEvent } from "@mui/material";
import { useLoading } from "../../contexts/LoadingContext";
import JobStatus from "./JobStatus";

export default function JobHistoryComponent() {
  // State management
  const [jobs, setJobs] = useState<JobOrder[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedJob, setSelectedJob] = useState<JobOrder | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jobStats, setJobStats] = useState<TripHistoryStats | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    startDate: number | null;
    endDate: number | null;
  }>({ startDate: null, endDate: null });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [view, setView] = useState("jobDetails");
  const navigate = useNavigate();
  const initialLoadDone = useRef(false);

  // Refs
  const dateTimePickerStartRef = useRef<{ reset: () => void }>(null);
  const dateTimePickerEndRef = useRef<{ reset: () => void }>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Context hooks
  const { showLoader, hideLoader } = useLoader();
  const { setLoading } = useLoading();

  // Memoized columns definition
  const columns = useMemo(
    () => [
      {
        header: "Job Code",
        accessor: "jobCode",
        cell: (value: string, row: Record<string, any>) => (
          <span
            className="text-[#027CB5] hover:underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              const selectedJob = jobs.find((job) => job.jobId === row.jobId);
              if (selectedJob) {
                openJobDetailsModal(selectedJob);
              }
            }}
          >
            {searchQuery ? highlightText(value, searchQuery) : value}
          </span>
        ),
      },
      {
        header: "Source",
        accessor: "source",
        sortable: false,
      },
      {
        header: "Destination",
        accessor: "destination",
        sortable: false,
      },
      {
        header: "Transporter",
        accessor: "transporter",
        sortable: false,
      },
      {
        header: "Start Date",
        accessor: "startDate",
        sortable: false,
      },
      {
        header: "End Date",
        accessor: "endDate",
        sortable: false,
      },
      {
        header: "Status",
        accessor: "jobStatus",
        sortable: false,
        cell: (value: string) => <JobStatus jobStatus={value} />,
      },
    ],
    [jobs, searchQuery]
  );

  // Memoized stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Jobs",
        value: totalElements,
        valueColor: "#59585c",
      },
      // {
      //   name: "Total Trips",
      //   value: jobStats?.trips || 0,
      //   valueColor: "#59585c",
      // },
      {
        name: "Total Containers",
        value: jobStats?.containers || 0,
        valueColor: "#59585c",
      },
      {
        name: "20ft",
        value: jobStats?.count20 || 0,
        valueColor: "#1d3557",
      },
      {
        name: "40ft",
        value: jobStats?.count40 || 0,
        valueColor: "#457b9d",
      },
      {
        name: "45ft",
        value: jobStats?.count45 || 0,
        valueColor: "#69b9bc",
      },
    ],
    [totalElements, jobStats]
  );

  // Load jobs data
  const loadJobs = useCallback(
    async (page: number, suppressLoader = false) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      if (!initialLoadDone.current) {
        showLoader();
      } else if (!suppressLoader) {
        setLoading(true);
      }

      try {
        console.log("Loading jobs with params:", {
          limit: pageSize,
          skip: (page - 1) * pageSize,
          jobCode: searchQuery,
          from: dateRange.startDate,
          to: dateRange.endDate,
        });

        const response = await JobService.getJobHistory({
          limit: pageSize,
          skip: (page - 1) * pageSize,
          jobCode: searchQuery,
          from: dateRange.startDate,
          to: dateRange.endDate,
          signal: abortControllerRef.current.signal,
        });

        // If the request was aborted, don't process the response
        if (abortControllerRef.current.signal.aborted) {
          return;
        }

        console.log("API Response:", response);

        if (!response?.data?.data) {
          console.error("Invalid API response structure:", response);
          return;
        }

        const newJobs = response.data.data.content || [];
        const totalElements = response.data.data.page?.totalElements || 0;
        const totalPages = Math.ceil(totalElements / pageSize);

        console.log("Setting new jobs:", {
          jobs: newJobs,
          totalPages,
          totalElements,
          currentPage: page,
        });

        setJobs(newJobs);
        setTotalPages(totalPages);
        setTotalElements(totalElements);

        // Load stats after jobs are loaded
        try {
          const statsResponse = await JobService.getJobHistoryStats();
          setJobStats(statsResponse.data);
        } catch (error) {
          console.error("Error loading job history stats", error);
          setJobStats(null);
        }
      } catch (error: any) {
        // Only handle non-abort errors
        if (!error.name || error.name !== "AbortError") {
          console.error("Error loading jobs", error);
          // Don't clear data on error, just log it
        }
      } finally {
        if (!initialLoadDone.current) {
          setTimeout(() => {
            hideLoader();
            setIsInitialLoading(false);
            initialLoadDone.current = true;
          }, 1000);
        } else if (!suppressLoader) {
          setLoading(false);
        }
      }
    },
    [pageSize, searchQuery, dateRange, showLoader, hideLoader, setLoading]
  );

  // Load jobs when dependencies change
  useEffect(() => {
    loadJobs(currentPage);
  }, [currentPage, loadJobs]);

  // Cleanup function to abort any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Event handlers
  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  const handleSearch = useCallback((query: string) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 500);
  }, []);

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1);
    },
    []
  );

  const openJobDetailsModal = useCallback((job: JobOrder) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedJob(null);
  }, []);

  const handleViewChange = useCallback((newView: string) => {
    setView(newView);
  }, []);

  const handleSetStartDate = useCallback((newStartDate: number) => {
    setDateRange((prev) => ({
      ...prev,
      startDate: newStartDate,
    }));
  }, []);

  const handleSetEndDate = useCallback((newEndDate: number) => {
    setDateRange((prev) => ({
      ...prev,
      endDate: newEndDate,
    }));
  }, []);

  const resetDates = useCallback(() => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
    if (dateTimePickerStartRef.current) dateTimePickerStartRef.current.reset();
    if (dateTimePickerEndRef.current) dateTimePickerEndRef.current.reset();
  }, []);

  const handleRowClick = useCallback((row: any) => {
    console.log(row);
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      await loadJobs(currentPage, true);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  }, [loadJobs, currentPage, setLoading]);

  // Memoized table data
  const tableData = useMemo(
    () =>
      jobs.map((job, index) => ({
        jobCode: job.jobCode || "N/A",
        jobType: job.jobType || "N/A",
        source: job.source || "N/A",
        destination: job.destination || "N/A",
        transporter:
          job.jobBreakups?.[0]?.jobBreakUpDetails?.[0]?.transporterIdName
            ?.name || "N/A",
        startDate: job.startDate ? formatEpochDate(job.startDate) : "N/A",
        endDate: job.endDate ? formatEpochDate(job.endDate) : "N/A",
        jobStatus: job.jobStatus || "N/A",
        jobId: job.jobId,
      })),
    [jobs]
  );

  return (
    <div className="p-[20px] job-history-page">
      <StatsBoxContainer stats={statsData} />

      <div className="flex justify-between items-center py-[20px]">
        <div className="mt-2">
          <SearchBar
            placeholder="Search by Job Code"
            onSearch={handleSearch}
            showClearButton={true}
          />
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 ">
            <div className="form-group ">
              <label htmlFor="startDate">Start Date</label>
              <DateTimePickerComponent
                skipMinDate={true}
                ref={dateTimePickerStartRef}
                onChange={(newValue: number | null) => {
                  if (newValue) handleSetStartDate(newValue);
                }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <DateTimePickerComponent
                skipMinDate={true}
                ref={dateTimePickerEndRef}
                onChange={(newValue: number | null) => {
                  if (newValue) handleSetEndDate(newValue);
                }}
              />
            </div>

            <button
              type="button"
              className="secondary_button !mt-[5px] min-w-max"
              onClick={resetDates}
              disabled={
                dateRange.startDate === null && dateRange.endDate === null
              }
            >
              Reset Dates
            </button>

            <button
              className="secondary_button !mt-[5px] min-w-max"
              onClick={handleRefresh}
            >
              Refresh
            </button>

            <button
              className="primary_button !mt-[5px] min-w-max"
              onClick={() => navigate("/jobs/history/upload")}
            >
              Upload Job History
            </button>
          </div>
        </div>
      </div>

      {isInitialLoading ? (
        <div className="center_floater_container max-h-[231px]">
          {/* Loading... */}
        </div>
      ) : jobs.length > 0 || totalElements > 0 ? (
        <div>
          <TableComponent
            columns={columns}
            data={tableData}
            onRowClick={handleRowClick}
            showCheckboxes={false}
            showSerialNumbers={true}
            searchQuery={searchQuery}
            className="job-history-table"
            showPagination={true}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableRowHover={true}
            highlightSearchMatches={false}
            isServerSidePagination={true}
            totalElements={totalElements}
          />
        </div>
      ) : (
        <div className="center_floater_container max-h-[231px]">
          No results !
        </div>
      )}

      {isModalOpen && selectedJob && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`${selectedJob.jobCode}`}
          className="job_preview_modal"
        >
          <div className="bg-card rounded-[10px] selected-job-details card-shadow">
            <div className="flex px-[20px] border-b border-bottomline justify-between">
              <div className="flex">
                <button
                  className={`mr-2 py-2 px-4 font-w-[500] ${
                    view === "jobDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => handleViewChange("jobDetails")}
                >
                  Job Details
                </button>
                <button
                  className={`py-2 px-4 font-w-[500] ${
                    view === "containerDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => handleViewChange("containerDetails")}
                >
                  Containers
                </button>
                <button
                  className={`py-2 px-4 font-w-[500] ${
                    view === "transporterDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => handleViewChange("transporterDetails")}
                >
                  Transporters
                </button>
              </div>
            </div>
            {view === "jobDetails" && <JobDetails job={selectedJob} />}
            {view === "containerDetails" && (
              <ContainerDetails
                job={selectedJob}
                hideContainerAssignment={true}
              />
            )}
            {view === "transporterDetails" && (
              <TransporterDetails jobOrder={selectedJob} />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
